import React, { Children, PropsWithChildren, ReactElement, useState } from 'react';
import styled from 'styled-components';

import { createSlotComponent, getChildSlot } from 'utils';
import { IconMinus, IconPlus } from 'widgets';

import { HorizontalLayout, VerticalLayout } from '../Layout';

const AccordionHead = createSlotComponent('AccordionHead');
const AccordionContent = createSlotComponent('AccordionContent');

type Props = {
    isOpen?: boolean;
    isOpenDefault?: boolean;
    setIsOpen?: (b: boolean) => void;
    onToggle?: (b: boolean) => void;
    $maxWidth?: string;
    $padding?: string;
    $background?: string;
} & (
    | { isOpen: boolean; setIsOpen: (b: boolean) => void }
    | {
          isOpen?: never;
          setIsOpen?: never;
      }
);

export const Accordion = (props: PropsWithChildren<Props>) => {
    const childrenArray = Children.toArray(props.children) as ReactElement[];
    const accordionHeadSlot = getChildSlot(childrenArray, AccordionHead.slotName);
    const accordionContentSlot = getChildSlot(childrenArray, AccordionContent.slotName);
    const [_isOpen, _setIsOpen] = useState(props?.isOpenDefault ?? false);
    const isOpen = typeof props?.setIsOpen !== 'undefined' ? props.isOpen : _isOpen;
    const setIsOpen = typeof props?.setIsOpen !== 'undefined' ? props.setIsOpen : _setIsOpen;

    return (
        <AccordionContainer $maxWidth={props?.$maxWidth} $background={props?.$background}>
            <AccordionHeadContainer
                $apart
                $rowCenter
                $padding={props?.$padding}
                $gap={16}
                onClick={() => {
                    props?.onToggle && props?.onToggle(!isOpen);
                    setIsOpen(!isOpen);
                }}
            >
                {accordionHeadSlot}
                <IconButtonContainer type="button">
                    {isOpen ? <IconMinus size="micro" /> : <IconPlus size="micro" />}
                </IconButtonContainer>
            </AccordionHeadContainer>
            {isOpen && (
                <>
                    <Separator />
                    <AccordionContentContainer $padding={props?.$padding}>
                        {accordionContentSlot}
                    </AccordionContentContainer>
                </>
            )}
        </AccordionContainer>
    );
};

Accordion.displayName = 'Accordion';
Accordion.Head = AccordionHead;
Accordion.Content = AccordionContent;

const AccordionContainer = styled(VerticalLayout)<{ $maxWidth?: string; $background?: string }>`
    ${({ $maxWidth }) => ($maxWidth ? `max-width: ${$maxWidth};` : null)}
    ${({ $background }) => ($background ? `background: ${$background};` : null)}
    width: 100%;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.colors.cocoa100};
    box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.03);
`;

const AccordionHeadContainer = styled(HorizontalLayout)<{ $padding?: string }>`
    padding: ${({ $padding = '1rem' }) => $padding};
    cursor: pointer;
`;

const AccordionContentContainer = styled.div<{ $padding?: string }>`
    padding: ${({ $padding = '1rem' }) => $padding};
    display: grid;
`;

const Separator = styled.div`
    width: 100%;
    border-top: 1px solid ${({ theme }) => theme.colors.cocoa200};
`;

const IconButtonContainer = styled.button`
    all: unset;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 24px;
    width: 24px;
    border-radius: 3px;
    cursor: pointer;
    border: 1px solid ${({ theme }) => theme.colors.cocoa100};
`;
